/**
 * A minimal client for Segment's HTTP tracking API. Events are sent fire-and-forget; analytics
 * failures never surface to the user.
 *
 * Every event carries a random anonymous ID persisted in localStorage, plus the user ID once one
 * has been identified. Identifying sends both IDs together, which is what lets Segment tie activity
 * from before login (like viewing the signup page) to the account.
 */

import { pathname, search } from "~/util/router.js";

const ANONYMOUS_ID_KEY = "segment_anonymous_id";

/**
 * UTM params and the Segment `context.campaign` fields they map onto. These are the only query
 * params kept when reporting URLs, since query strings can carry secrets like invite tokens.
 */
const CAMPAIGN_FIELDS = /** @type {const} */ ({
	utm_source: "source",
	utm_medium: "medium",
	utm_campaign: "name",
	utm_term: "term",
	utm_content: "content",
});

/**
 * @type {{
 * 	writeKey: string;
 * 	anonymousId: string;
 * 	userId: string | null;
 * 	properties: Record<string, unknown>;
 * } | null}
 */
let config = null;

/**
 * Initialize Segment. Until this is called, tracking is a no-op.
 *
 * @param {object} options
 * @param {string} options.writeKey
 * @param {Record<string, unknown>} [options.properties] Properties added to every event.
 */
export function init({ writeKey, properties = {} }) {
	config = { writeKey, anonymousId: getAnonymousId(), userId: null, properties };
}

/**
 * Set a property on all future events.
 *
 * @param {string} key
 * @param {unknown} value
 */
export function setProperty(key, value) {
	if (!config) return;
	config.properties[key] = value;
}

/**
 * Attribute this device and all future events to the given user. Does nothing if that user is
 * already identified.
 *
 * @param {string} userId
 * @param {Record<string, unknown>} [traits] Attributes of the user, like their email.
 */
export function identify(userId, traits) {
	if (!config || config.userId === userId) return;
	config.userId = userId;
	send("identify", { traits });
}

export function anonymousId() {
	return config?.anonymousId;
}

/**
 * Forget the identified user, e.g. on logout. The device gets a fresh anonymous ID so the next
 * person to use it isn't tied to the previous account.
 */
export function reset() {
	if (!config || config.userId === null) return;
	config.userId = null;
	config.anonymousId = getAnonymousId(true);
}

/**
 * Record an event.
 *
 * @param {string} event
 * @param {Record<string, unknown>} [properties]
 */
export function track(event, properties) {
	if (!config) return;
	send("track", { event, properties: { ...config.properties, ...properties } });
}

/** Record a view of the current route. */
export function trackPageView() {
	track("Page viewed", { path: pathname.peek(), referrer: referrer() });
}

/**
 * @param {"track" | "identify"} type
 * @param {Record<string, unknown>} fields
 */
function send(type, fields) {
	if (!config) return;

	const body = {
		writeKey: config.writeKey,
		type,
		anonymousId: config.anonymousId,
		userId: config.userId ?? undefined,
		...fields,
		context: {
			userAgent: navigator.userAgent,
			locale: navigator.language,
			// peek so callers inside effects don't subscribe to route changes
			page: { path: pathname.peek(), url: sanitizeUrl(location.href), referrer: referrer() },
			campaign: campaign(),
			library: { name: "phaze", version: "1" },
		},
		timestamp: new Date().toISOString(),
		messageId: crypto.randomUUID(),
	};

	// text/plain keeps this a simple request, avoiding a CORS preflight
	fetch(`https://api.segment.io/v1/${type}`, {
		method: "POST",
		headers: { "Content-Type": "text/plain" },
		body: JSON.stringify(body),
		keepalive: true,
	}).catch(() => {});
}

function referrer() {
	return document.referrer ? sanitizeUrl(document.referrer) : undefined;
}

/**
 * The URL with only UTM query params. The hash is dropped: under the native app's hash routing it
 * holds the route, which is reported separately as the path.
 *
 * @param {string} href
 */
function sanitizeUrl(href) {
	const url = new URL(href);
	const params = new URLSearchParams();
	for (const [key, value] of url.searchParams) {
		if (Object.hasOwn(CAMPAIGN_FIELDS, key)) params.append(key, value);
	}
	url.search = params.toString();
	url.hash = "";
	return url.href;
}

/** UTM params from the current route, in the shape Segment expects for `context.campaign`. */
function campaign() {
	const params = search.peek();

	/** @type {Record<string, string>} */
	const fields = {};
	for (const [param, field] of Object.entries(CAMPAIGN_FIELDS)) {
		const value = params[param];
		if (value) fields[field] = value;
	}
	return Object.keys(fields).length > 0 ? fields : undefined;
}

/** @param {boolean} [fresh] Replace the stored ID with a new one. */
function getAnonymousId(fresh = false) {
	try {
		let id = fresh ? null : localStorage.getItem(ANONYMOUS_ID_KEY);
		if (!id) {
			id = crypto.randomUUID();
			localStorage.setItem(ANONYMOUS_ID_KEY, id);
		}
		return id;
	} catch {
		return crypto.randomUUID();
	}
}
